import { STRIPE_CHECKOUT_PAY_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import { PaymentErrorCodes } from '@app/types/monolith/PaymentErrorCodes';
import { getHost } from '@redbubble/utils/getHost';
import { useCallback, useState } from 'react';
import { addDDError } from '../../pages/cart/utils/datadog';
import { PageSourceType, PayError, RbPaymentMethodType } from './types';

export class UsePayUnexpectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UsePayUnexpectedError';
  }
}

export interface PayRequestBody {
  page_source: PageSourceType;
  payment_method_type: RbPaymentMethodType;
  order_details: {
    email: string;
    shipping_details: AddressDetails;
    billing_details: AddressDetails;
  };
}

export interface AddressDetails {
  name: string;
  phone_number: string;
  address: Address;
}

interface Address {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  country_code: string;
  postal_code: string;
}

interface PayResponseData {
  client_secret: string;
  // payment intent id
  id: string;
  purchase_order_id: number;
  status: string;
}

export interface PayResponseBody {
  data?: PayResponseData;
  errors?: PayError[];
}

export interface PayResult {
  data?: PayResponseData;
  loading: boolean;
  errors?: PayError[];
}

const url = [getHost(), STRIPE_CHECKOUT_PAY_PUBLIC_ENDPOINT].join('');

export const usePay = () => {
  const [result, setResult] = useState<PayResult>({
    loading: false,
  });

  const post = useCallback(async (req: PayRequestBody): Promise<PayResult> => {
    setResult({ data: undefined, loading: true, errors: undefined });

    // Since we use stripe.confirmPayment() in StripeExpress, we need to set "automatic_payments" as "true".
    // This tells the endpoint not to set shipping, billing and payment method on the PaymentIntent. This is
    // because the Stripe SDK expects to set those in the UI using the "publishable key", and that operation
    // will fail if they have been set using our server-side "secret key". Publishable keys are not permitted
    // to update PaymentIntent fields set with a higher permission secret keys.
    // Also it is recommended by Stripe to use automatic payment methods instead of specifying manual methods
    // so the endpoint will do that as well when "automatic_payments" is "true".
    // Some useful links:
    // - https://stripe.com/docs/js/payment_intents/confirm_payment
    // - https://stripe.com/docs/api/payment_intents/create

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...req, automatic_payments: true }),
    })
      .then((res) => {
        return res.json();
      })
      .then(({ data, errors }: PayResponseBody) => {
        const newRes: PayResult = {
          data,
          loading: false,
          errors,
        };

        setResult(newRes);
        return newRes;
      })
      .catch((err) => {
        const newRes: PayResult = {
          data: undefined,
          loading: false,
          errors: [{ code: PaymentErrorCodes.SERVER_ERROR }],
        };

        addDDError(
          new UsePayUnexpectedError(
            `Unexpected error while fetching Payment Intent Id using usePay() "${err}"`
          )
        );

        setResult(newRes);
        return newRes;
      });
  }, []);

  return {
    ...result,
    post,
  };
};
